import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import React, { useEffect } from "react";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { NonNullableOrganisationState } from "../../redux/state";
import { OrganisationAppBar } from "../OrganisationAppBar";
import {
  perceivedUnderstandingService,
  qualTypeService,
  roepaoOptionsService,
  titlesService,
  yesNoService,
} from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { UserRole } from "../../domain/userRole";
import { Controller, useForm } from "react-hook-form";
import { isClosedForEngagement, Organisation } from "../../domain/Organisation";
import { useDispatch } from "react-redux";
import { modelToJsonPatchDocument } from "@ofqual/portal-core/core/utils/modelHelpers";
import { editOrganisationEngagement } from "../../redux/actions";
import { Error } from "../common/Error";
import {
  formatUtcDate,
  isFutureDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { useHistory, useRouteMatch } from "react-router-dom";
import { RecognitionStatus } from "../../domain/RecognitionStatus";
import { CloseButtonAndDialog } from "./dialogs/CloseButtonAndDialog";
import { ReopenButtonAndDialog } from "./dialogs/ReopenButtonAndDialog";
import { ReleaseButtonAndDialog } from "./dialogs/ReleaseButtonAndDialog";
import { Prompt } from "react-router-dom";
import { authSelector } from "../../redux/selectors";
import { ShareButtonAndDialog } from "./dialogs/ShareButtonAndDialog";
import { UserType } from "../../redux/types";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
  pendingChangeField: {
    "& fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  pendingChangeFieldIcon: {
    color: theme.palette.primary.main,
  },
}));

export const OrganisationRecognitionDetails: React.FC<{
  orgState: NonNullableOrganisationState;
}> = ({ orgState }) => {
  const { push } = useHistory();
  const { url } = useRouteMatch();
  const { org } = orgState;

  const { isInRole, userType } = useAuth(authSelector);
  const dispatch = useDispatch();
  const isMagicLinkUser = userType === UserType.Magic;

  const inEditorRole = isInRole(
    UserRole.ITAdmin,
    UserRole.OfqualRecognitionAdmin
  );

  const canEdit =
    orgState.status !== "UPDATING" &&
    org.pendingRecognition === true &&
    inEditorRole &&
    !isClosedForEngagement(org);

  const {
    handleSubmit,
    control,
    formState,
    watch,
    errors,
    reset,
  } = useForm<Organisation>({
    defaultValues: org!,
  });

  useEffect(() => {
    if (org) {
      // once user is available in redux, load it in to the form via the `reset` function
      reset(org);
    }
  }, [org, reset]);

  const { dirtyFields } = formState;

  const onSubmit = (newOrg: Organisation) => {
    // todo: refactor this into a separate module/method
    const changedFields = (Object.keys(
      dirtyFields
    ) as (keyof Organisation)[]).filter((field) => {
      return (
        // because this is a simple form  with one-to-one fields we can filter
        //  for only values that have changed from the incoming model (just because
        //  a field is dirty it still may have been returned to its initial )
        ((orgState.org && orgState.org[field]) ?? null) !==
        (newOrg[field] ?? null)
      );
    });

    const patch = modelToJsonPatchDocument(newOrg, changedFields);
    dispatch(editOrganisationEngagement(org.itemId!, patch));
  };

  const [
    yesNoLookups,
    qualTypesLookups,
    perceivedUnderstandingLookups,
    contactTitleLookup,
    roepaoLookups,
  ] = useLookup(
    yesNoService,
    qualTypeService,
    perceivedUnderstandingService,
    titlesService,
    roepaoOptionsService
  );

  const { qualTypesToApplyFor } = watch(["qualTypesToApplyFor"]);
  const hasOtherQualType =
    qualTypesToApplyFor.find((x) => x.qualificationTypeId === 99) != null;
  const classes = useStyles();

  const navigateToApplication = (applicationId: string) =>
    push(`${url}/applications/${applicationId}`);

  const isAtRecognitionStageAndRole =
    org &&
    org.pendingRecognition &&
    !org.allowedToApply &&
    isInRole(
      UserRole.OfqualRecognitionAdmin,
      UserRole.OfqualRecognitionUser,
      UserRole.ITAdmin
    );
  const areCloseReleaseButtonsVisible =
    isAtRecognitionStageAndRole && !isClosedForEngagement(org);

  const isReopenButtonVisible =
    isAtRecognitionStageAndRole && isClosedForEngagement(org);

  return (
    <>
      <div>
        <Prompt
          message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
          when={Object.keys(formState.dirtyFields).length > 0}
        />
        <OrganisationAppBar
          breadcrumbs={[
            { label: `${org.name}`, to: `${org.itemId}` },
            { label: "Recognition" },
          ]}
        >
          {areCloseReleaseButtonsVisible && (
            <CloseButtonAndDialog id={org.id} />
          )}
          {isReopenButtonVisible && <ReopenButtonAndDialog id={org.id} />}
          {areCloseReleaseButtonsVisible && (
            <ReleaseButtonAndDialog id={org.id} />
          )}
          {!isMagicLinkUser && (
            <>
              <ShareButtonAndDialog id={org.id} />
              <AppBarSaveButton
                onClick={handleSubmit(onSubmit)}
                isSubmitting={orgState.status === "UPDATING"}
                isSubmitted={orgState.lastCrudResult.isSuccess}
                dataTest="save-button"
                disabled={!canEdit}
              />
            </>
          )}
        </OrganisationAppBar>
        <form>
          <div data-test="recognition-details">
            <div id="data-panel">
              <Typography
                data-test="recognition-details-contact-header"
                variant="h6"
              >
                Recognition contact
              </Typography>
              <Paper
                className={classes.formSection}
                data-test="recognition-details-contact-section"
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6} xl={2}>
                    <TextField
                      variant="outlined"
                      label={"Title"}
                      data-test="recognition-details-contact-title"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      value={
                        contactTitleLookup.find(
                          (x) => x.id === org.contactTitle
                        )?.label
                      }
                    />
                  </Grid>
                  <Grid item xs={12} lg={6} xl={5}>
                    <TextField
                      variant="outlined"
                      label={"First name"}
                      data-test="recognition-details-contact-firstname"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      value={org.contactFirstName}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6} xl={5}>
                    <TextField
                      variant="outlined"
                      label={"Surname"}
                      data-test="recognition-details-contact-surname"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      value={org.contactSurname}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6} xl={4}>
                    <TextField
                      variant="outlined"
                      label={"Email"}
                      data-test="recognition-details-contact-email"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      value={org.contactEmail}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6} xl={4}>
                    <TextField
                      variant="outlined"
                      label={"Role in organisation"}
                      data-test="recognition-details-contact-role"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      value={org.contactRoleInOrg}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </div>
            <div id="data-panel">
              <Typography
                data-test="recognition-details-engagement-header"
                variant="h6"
              >
                Engagement
              </Typography>
              <Paper
                className={classes.formSection}
                data-test="recognition-details-engagement-section"
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" disabled={true}>
                      <FormLabel
                        id="intendingToOrDeliveringEPAs-label"
                        component="legend"
                      >
                        Is the organisation intending to, or do they already
                        deliver, apprenticeship end point assessments?
                      </FormLabel>
                      <RadioGroup
                        row
                        value={org?.intendingToOrDeliveringEPAs}
                        aria-labelledby="intendingToOrDeliveringEPAs-label"
                        name="intendingToOrDeliveringEPAs"
                        data-test="radiogroup-intendingToOrDeliveringEPAs"
                      >
                        <FormControlLabel
                          value={true}
                          label="Yes"
                          control={<Radio />}
                          checked={org?.intendingToOrDeliveringEPAs === true}
                        />
                        <FormControlLabel
                          value={false}
                          label="No"
                          control={<Radio />}
                          checked={org?.intendingToOrDeliveringEPAs === false}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="engagementCaseNotes"
                      rules={{ required: false, maxLength: 5000 }}
                      as={
                        <TextField
                          variant="outlined"
                          label={"Case notes"}
                          data-test="recognition-details-engagement-notes"
                          fullWidth
                          multiline
                          rows={4}
                          rowsMax={8}
                          InputProps={{
                            readOnly: !canEdit,
                          }}
                        />
                      }
                    />
                    {errors.engagementCaseNotes?.type === "maxLength" && (
                      <Error>
                        Case notes must be less than 5000 characters
                      </Error> // TODO confirm this
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={"hasSubmittedApplicationBefore"}
                      as={
                        <Select
                          label="Previously submitted application?"
                          datatest="recognition-details-submitted-app"
                          InputProps={{
                            readOnly: !canEdit,
                          }}
                        >
                          {yesNoLookups}
                        </Select>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={"isOnRoEPAOs"}
                      as={
                        <Select
                          label="Is the organisation on the RoEPAOs?"
                          datatest="recognition-details-on-roepaos"
                          InputProps={{
                            readOnly: !canEdit,
                          }}
                        >
                          {roepaoLookups}
                        </Select>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={"qualTypesToApplyFor"}
                      render={(props) => (
                        <Select
                          selectVariant="multiple"
                          label="Qualification types to be recognised for"
                          datatest="recognition-details-qual-types"
                          value={props.value.map(
                            (v: { qualificationTypeId: number }) =>
                              v.qualificationTypeId
                          )}
                          onChange={(e: any) => {
                            const val: Array<number> = e.target.value;
                            props.onChange(
                              val.map((x) => ({ qualificationTypeId: x }))
                            );
                          }}
                          InputProps={{
                            readOnly: !canEdit,
                          }}
                        >
                          {qualTypesLookups}
                        </Select>
                      )}
                    />
                  </Grid>
                  {hasOtherQualType && (
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name={"qualTypeIntendingToApplyForOther"}
                        rules={{
                          required: hasOtherQualType,
                        }}
                        as={
                          <TextField
                            variant="outlined"
                            label="Other qualification type (please specify)"
                            data-test="recognition-details-qual-types-other"
                            InputProps={{
                              readOnly: !canEdit,
                            }}
                            fullWidth
                            required={hasOtherQualType}
                          />
                        }
                      />
                      {errors.qualTypeIntendingToApplyForOther?.type ===
                        "required" && (
                        <Error>
                          This field is mandatory if "Other" is selected in
                          qualification types
                        </Error>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} md={8}>
                    <Controller
                      control={control}
                      name={"engagementPerceivedUnderstanding"}
                      as={
                        <Select
                          label="Perceived understanding"
                          datatest="recognition-details-perceived-understanding"
                          InputProps={{
                            readOnly: !canEdit,
                          }}
                        >
                          {perceivedUnderstandingLookups}
                        </Select>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Controller
                      control={control}
                      name={"dateIntendingToApply"}
                      as={
                        <DatePicker
                          label="Date intending to apply"
                          dataTest="recognition-details-intend-to-apply-date"
                          readOnly={!canEdit}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Controller
                      control={control}
                      name="engagementFollowUpDate"
                      rules={{
                        validate: (value) =>
                          isFutureDate(value) ? true : false,
                      }}
                      as={
                        <DatePicker
                          label="Follow-up date"
                          dataTest="engagement-follow-up-date"
                          readOnly={!canEdit}
                        />
                      }
                    />
                    {errors.engagementFollowUpDate?.type === "validate" && (
                      <Error>Date must be in the future</Error>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </div>
            <div id="data-panel">
              <Typography
                data-test="recognition-details-applications-header"
                variant="h6"
              >
                Applications
              </Typography>
              <Paper>
                {!org.recognitionApplications?.length && (
                  <Typography variant="body1" style={{ padding: 25 }}>
                    This organisation has no applications
                  </Typography>
                )}
                <List disablePadding>
                  {org.recognitionApplications?.map((application) => (
                    <ListItem
                      key={application.id}
                      button
                      data-test="application-item"
                      alignItems="flex-start"
                      divider
                      onClick={() => navigateToApplication(application.id)}
                    >
                      <ListItemText
                        primary={`Application ${application.applicationNumber}`}
                        secondary={
                          <>
                            Created:
                            {formatUtcDate(
                              application.createdDate,
                              standardDateFormats.DATE
                            )}
                            &nbsp;Status:{" "}
                            {RecognitionStatus[application.status]}
                          </>
                        }
                      ></ListItemText>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
